/**
 * DeviceCoreId - Stable device fingerprinting that ignores screen/display changes
 * 
 * This module provides functionality to generate a stable device identifier that remains
 * consistent even when screen resolution, color settings, or external monitors change.
 */

import canonicalize from 'canonicalize';

// Lightweight SHA-256 (browser-native)
async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Deep sort object keys; also sort known array fields where order is non-semantic


export type FPResult = {
  visitorId: string;
  components: Record<string, any>;
};

// Fields to exclude due to display/monitor/HDR variability
const EXCLUDE_KEYS = new Set<string>([
  'screenResolution',
  'screenFrame',
  'colorDepth',
  'colorGamut',
  'hdr',
  'contrast',
  'monochrome',
  'reducedMotion',
  'reducedTransparency',
  'invertedColors',
  // Additional display-context-dependent components
  'fontPreferences', // Font rendering measurements change with display DPI/scaling
  'canvas'           // Canvas rendering changes with display context
]);

function stripUnstableDisplayFields(components: Record<string, any>) {
  const stable: Record<string, any> = {};
  for (const [key, val] of Object.entries(components)) {
    if (EXCLUDE_KEYS.has(key)) continue;
    
    // Strip timing/duration information that varies between runs
    if (val && typeof val === 'object') {
      if (val.value !== undefined) {
        // Only keep the value, exclude duration and other timing data
        stable[key] = { value: val.value };
      } else if (val.value === undefined && val.duration !== undefined) {
        // Skip components that have undefined value but contain duration (e.g., domBlockers, osCpu)
        continue;
      } else if (Object.keys(val).every(k => k === 'duration' || k === 'error')) {
        // Skip components that only contain timing/error data
        continue;
      } else {
        // Keep other objects as-is
        stable[key] = val;
      }
    } else {
      stable[key] = val;
    }
  }

  // Normalize known unordered arrays to improve determinism
  // e.g., webGlExtensions.value.extensions is an array of strings
  try {
    const extArr = stable.webGlExtensions?.value?.extensions;
    if (Array.isArray(extArr)) {
      stable.webGlExtensions.value.extensions = [...extArr].sort();
    }
    const pluginList = stable.plugins?.value;
    if (Array.isArray(pluginList)) {
      stable.plugins.value = [...pluginList].sort((a, b) =>
        JSON.stringify(a).localeCompare(JSON.stringify(b))
      );
    }
    const fontsList = stable.fonts?.value;
    if (Array.isArray(fontsList)) {
      stable.fonts.value = [...fontsList].sort();
    }
    const languages = stable.languages?.value;
    if (Array.isArray(languages)) {
      stable.languages.value = languages; // canonicalize will handle proper ordering
    }
  } catch {
    // best-effort normalization; avoid crashing if shape differs
  }

  return stable;
}

// Export for debugging
export { stripUnstableDisplayFields };

export async function getStableIds(
  fpResult: FPResult
): Promise<{ visitorId: string; deviceCoreId: string; }> {
  // 1. Filter out fields that can change within the same device (screen, display, etc.)
  const filtered = stripUnstableDisplayFields(fpResult.components);
  
  // 2. Normalize for consistent ordering using RFC 8785 JSON Canonicalization
  const canonicalJson = canonicalize(filtered);
  if (!canonicalJson) {
    throw new Error('Failed to canonicalize fingerprint data');
  }
  
  // 3. Calculate stable hash from the canonical JSON
  const stableId = await sha256Hex(canonicalJson);
  
  // 4. Return the same stable ID for both visitorId and deviceCoreId
  return { visitorId: stableId, deviceCoreId: stableId };
}

// Utility function to get excluded fields for debugging/inspection
export function getExcludedFields(): string[] {
  return Array.from(EXCLUDE_KEYS);
}

// Utility function to check if a field would be excluded
export function isFieldExcluded(fieldName: string): boolean {
  return EXCLUDE_KEYS.has(fieldName);
}
